import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import "./Navbar.css";

function Navbar() {
  const { t, i18n } = useTranslation();
  const { user, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navbarRef = useRef<HTMLElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const toggleLang = () => {
    const newLang = i18n.language === "ar" ? "en" : "ar";
    void i18n.changeLanguage(newLang);
    document.documentElement.dir = newLang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = newLang;
  };

  const handleLogout = async () => {
    await logout();
    setDropdownOpen(false);
    navigate("/");
  };

  const close = () => setMenuOpen(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;

      // Close profile dropdown
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setDropdownOpen(false);
      }

      // Close mobile menu
      if (
        navbarRef.current &&
        !navbarRef.current.contains(target)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav className="navbar" ref={navbarRef}>
      <div className="navbar-container container">
        <Link to="/" className="navbar-logo">
          <img src="/navbar-logo.png" alt="FCS" />
        </Link>

        <ul className={`navbar-links ${menuOpen ? "active" : ""}`}>
          <li>
            <Link
              to="/"
              onClick={close}
              className={location.pathname === "/" ? "active" : ""}
            >
              {t("nav.home")}
            </Link>
          </li>
          <li>
            <Link
              to="/about"
              onClick={close}
              className={location.pathname === "/about" ? "active" : ""}
            >
              {t("nav.about")}
            </Link>
          </li>
          <li>
            <Link
              to="/services"
              onClick={close}
              className={location.pathname === "/services" ? "active" : ""}
            >
              {t("nav.services")}
            </Link>
          </li>
          <li>
            <Link
              to="/consultation"
              onClick={close}
              className={location.pathname === "/consultation" ? "active" : ""}
            >
              {t("nav.consultation")}
            </Link>
          </li>
          <li>
            <Link
              to="/hiring"
              onClick={close}
              className={location.pathname === "/hiring" ? "active" : ""}
            >
              {t("nav.hiring")}
            </Link>
          </li>
          <li>
            <Link
              to="/contact"
              onClick={close}
              className={location.pathname === "/contact" ? "active" : ""}
            >
              {t("nav.contact")}
            </Link>
          </li>
          {user && hasRole(["ADMIN"]) && (
            <li>
              <Link
                to="/admin"
                onClick={close}
                className={location.pathname === "/admin" ? "active" : ""}
              >
                {t("nav.dashboard")}
              </Link>
            </li>
          )}
        </ul>

        <div className="navbar-right">
          <button className="lang-toggle" onClick={toggleLang}>
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="2" y1="12" x2="22" y2="12" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            {i18n.language === "ar" ? "EN" : "عربي"}
          </button>

          {user ? (
            <div className="navbar-profile" ref={dropdownRef}>
              <button
                className="navbar-profile-btn"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                <span className="navbar-avatar">
                  {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                </span>
                <svg
                  className={`navbar-chevron ${dropdownOpen ? "open" : ""}`}
                  viewBox="0 0 12 12"
                  width="10"
                  height="10"
                >
                  <path
                    d="M2 4l4 4 4-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>

              {dropdownOpen && (
                <div className="navbar-dropdown">
                  <div className="navbar-dropdown-header">
                    <span className="navbar-dropdown-avatar">
                      {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                    </span>
                    <div>
                      <p className="navbar-dropdown-name">{user.name}</p>
                      <p className="navbar-dropdown-email">{user.email}</p>
                    </div>
                  </div>
                  <div className="navbar-dropdown-divider"></div>
                  {hasRole(["ADMIN"]) && (
                    <Link
                      to="/admin"
                      className="navbar-dropdown-item"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <rect x="3" y="3" width="7" height="7" rx="1" />
                        <rect x="14" y="3" width="7" height="7" rx="1" />
                        <rect x="3" y="14" width="7" height="7" rx="1" />
                        <rect x="14" y="14" width="7" height="7" rx="1" />
                      </svg>
                      {t("nav.dashboard")}
                    </Link>
                  )}
                  <Link
                    to="/profile"
                    className="navbar-dropdown-item"
                    onClick={() => setDropdownOpen(false)}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    {t("nav.profile")}
                  </Link>
                  <Link
                    to="/my-bookings"
                    className="navbar-dropdown-item"
                    onClick={() => setDropdownOpen(false)}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    {t("nav.myBookings")}
                  </Link>
                  <button
                    className="navbar-dropdown-item navbar-dropdown-logout"
                    onClick={handleLogout}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    {t("nav.logout")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" className="navbar-login-btn">
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="login-icon"
              >
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M13.8 12H3" />
              </svg>
              <span>{t("nav.login")}</span>
            </Link>
          )}

          <button
            className={`navbar-toggle ${menuOpen ? "open" : ""}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle navigation"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
